import { useState, useRef, useEffect } from 'react';

import {
  useExperimentsTranslations,
  useRenameExperiment,
  useExperimentsUserPermission,
} from 'modules/experiments/hooks';
import { MessageMedium } from 'modules/domains/campaign';
import { useClickOutside } from 'utils/hooks/useClickOutside';
import { useUnsavedChangesPrompt } from 'utils/hooks/useUnsavedChangesPrompt';

import { ExperimentEditableInput } from './ExperimentEditableInput';
import { ReadOnlyExperimentName } from './Styled';

type ExperimentNameProps = {
  id: number;
  name: string;
  messageMedium: MessageMedium;
};

export const ExperimentName = ({ id, name, messageMedium }: ExperimentNameProps) => {
  const { ct } = useExperimentsTranslations();
  const { viewOnlyPermission } = useExperimentsUserPermission(messageMedium);
  const renameExperiment = useRenameExperiment({ id });

  // Input
  const [editableName, setEditableName] = useState(name);
  const unsavedChanges = name !== editableName;
  const inputRef = useRef<any>(null);

  const handleReset = () => {
    setEditableName(name);
  };

  const handleExitInput = () => {
    if (editableName.trim().length === 0) {
      handleReset();
    } else if (unsavedChanges) {
      renameExperiment(editableName);
    }
  };
  useUnsavedChangesPrompt(unsavedChanges, ct('errorMessage.unsavedPrompt'));
  useClickOutside(inputRef, handleExitInput);

  /**
   * The name and editableName can get out of sync
   * when we redirect between pages in react.
   *
   * This could be a super weird react issue. But,
   * this fixes the issue.
   */
  useEffect(() => {
    setEditableName(name);
  }, [name]);

  if (viewOnlyPermission) {
    return (
      <ReadOnlyExperimentName data-test="read-only-experiment-name">
        {editableName}
      </ReadOnlyExperimentName>
    );
  }

  return (
    <ExperimentEditableInput
      setEditableName={setEditableName}
      handleReset={handleReset}
      editableName={editableName}
    />
  );
};
